import {
  useFormatZoned,
  useTimeAxisContext,
} from "utils/customHooks/genericHooks";
import { useRetrotagSegmentRange } from "utils/customHooks/retrotagSliceSelectors";
import { ROW_HEIGHT } from "../../../../constants/constants";
import styled from "styled-components/macro";
import { FORMAT_TIME_WITH_SECONDS } from "utils/timeUtils";
import { FormatZonedFn } from "utils/customHooks/genericHooksTypes";
import { useIsDarkMode } from "store/atoms";

const SLIDER_Y1 = 10;
const SLIDER_Y2 = 30;
const SLIDER_HZ_DISTANCE_FROM_RETROTAG_SEGMENT = 4;
const SLIDER_INTERACTION_WIDTH = 25;
const SLIDER_INTERACTION_AREA_OVERLAPPING_SEGMENT = 10;

/**
 * A segment selector UI for the Timeline. A retrotagSegment is the
 * "new" segment that will be applied using this interface
 */
export default function RetrotagSegmentSelector() {
  const { scaleTime } = useTimeAxisContext();
  const range = useRetrotagSegmentRange();
  const formatZoned: FormatZonedFn = useFormatZoned();
  const [isDarkMode] = useIsDarkMode();
  if (!range?.ge || !range?.lt) {
    return null;
  }

  const retrotagSegment = {
    x1: scaleTime(range.ge),
    x2: scaleTime(range.lt),
  };

  if (!retrotagSegment.x1 || !retrotagSegment.x2) {
    return null;
  }

  const leftSliderIndicatorX =
    retrotagSegment.x1 - SLIDER_HZ_DISTANCE_FROM_RETROTAG_SEGMENT;
  const rightSliderIndicatorX =
    retrotagSegment.x2 + SLIDER_HZ_DISTANCE_FROM_RETROTAG_SEGMENT;

  const sliderAreaCommonProps = {
    y: 0,
    height: ROW_HEIGHT,
    width: SLIDER_INTERACTION_WIDTH,
    style: { cursor: "ew-resize" },
  };
  const sliderIndicatorCommonProps = {
    y1: SLIDER_Y1,
    y2: SLIDER_Y2,
    stroke: "black",
    style: {
      strokeWidth: "2px",
      strokeLinecap: "round",
      cursor: "ew-resize",
    } as React.CSSProperties,
  };

  const centerY = ROW_HEIGHT / 2 + FONT_SIZE / 2 - 1;

  return (
    <RetrotagSegmentSelectorStyledG
      data-component-name={"RetrotagSegmentSelector"}
      $isDarkMode={isDarkMode}
    >
      <defs>
        <filter id="shadow" x="0%" y="0%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="2 2" result="shadow" />
          <feOffset dx="0" dy="0" />
        </filter>
      </defs>

      <rect
        id="retrotagSegment"
        x={retrotagSegment.x1}
        y={0}
        height={ROW_HEIGHT}
        width={retrotagSegment.x2 - retrotagSegment.x1}
        rx={2}
        style={{
          fill: "black",
          fillOpacity: 0.2,
          stroke: "black",
          strokeWidth: "2px",
        }}
      />

      {/* start time + drop shadow */}
      <text
        className="retrotagSegmentTime startTime shadow"
        x={retrotagSegment.x1 - FONT_SIZE * 5}
        y={centerY}
        style={{ filter: "url(#shadow)" }}
        stroke={isDarkMode ? "#000" : "#fff"}
      >
        {formatZoned(Number(range.ge), FORMAT_TIME_WITH_SECONDS)}
      </text>
      <text
        className="retrotagSegmentTime startTime"
        x={retrotagSegment.x1 - FONT_SIZE * 5}
        y={centerY}
      >
        {formatZoned(Number(range.ge), FORMAT_TIME_WITH_SECONDS)}
      </text>

      {/* end time + drop shadow */}
      <text
        className="retrotagSegmentTime endTime shadow"
        x={retrotagSegment.x2 + FONT_SIZE}
        y={centerY}
        style={{ filter: "url(#shadow)" }}
        stroke={isDarkMode ? "#000" : "#fff"}
      >
        {formatZoned(Number(range.lt), FORMAT_TIME_WITH_SECONDS)}
      </text>
      <text
        className="retrotagSegmentTime endTime"
        x={retrotagSegment.x2 + FONT_SIZE}
        y={centerY}
      >
        {formatZoned(Number(range.lt), FORMAT_TIME_WITH_SECONDS)}
      </text>

      <rect
        {...sliderAreaCommonProps}
        className="retrotagSliderInteractionArea leftSlider"
        x={
          retrotagSegment.x1 -
          SLIDER_INTERACTION_WIDTH +
          SLIDER_INTERACTION_AREA_OVERLAPPING_SEGMENT
        }
        rx={6}
      />
      <line
        {...sliderIndicatorCommonProps}
        className="retrotagSliderIndicator leftSlider"
        x1={leftSliderIndicatorX}
        x2={leftSliderIndicatorX}
      />
      <rect
        {...sliderAreaCommonProps}
        className="retrotagSliderInteractionArea rightSlider"
        x={retrotagSegment.x2 - SLIDER_INTERACTION_AREA_OVERLAPPING_SEGMENT}
        rx={6}
      />
      <line
        {...sliderIndicatorCommonProps}
        className="retrotagSliderIndicator rightSlider"
        x1={rightSliderIndicatorX}
        x2={rightSliderIndicatorX}
      />
    </RetrotagSegmentSelectorStyledG>
  );
}
const FONT_SIZE = 12;
const RetrotagSegmentSelectorStyledG = styled.g<{ $isDarkMode: boolean }>`
  .retrotagSegmentTime {
    font-size: ${FONT_SIZE}px;
    font-weight: normal;
    &:not(.shadow) {
      stroke: ${p => (p.$isDarkMode ? "white" : "black")};
    }
  }
  .retrotagSliderInteractionArea {
    fill: transparent;
    transition: fill 0.2s;
    &:hover,
    &:active {
      fill: ${p =>
        p.$isDarkMode
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(255, 255, 255, 0.25)"};
    }
  }
  .retrotagSliderIndicator,
  .retrotagSegmentTime {
    pointer-events: none;
  }
`;
